/**
 * Orbe del asistente de migración (paso 3): la red neuronal dentro de la esfera de
 * cristal. Este módulo es solo el ciclo de vida; la red y el dibujo viven en
 * lib/orbe-red.js (paleta `cristal`), compartidos con la escena de Timbrando.
 *
 *   var orbe = montarOrbe(canvas, { semilla: emisorId, alFallar: usarFallbackCss });
 *   orbe.progreso(0.58);   // enciende los nodos que el avance ya alcanzó (0 a 1); el
 *                          // primero tras montar, en silencio (sin destello ni señales)
 *   orbe.rafaga();         // llegó un bloque de CFDIs
 *   orbe.destello();       // terminó un renglón
 *   orbe.detener();        // al salir de la pantalla
 *
 * Un solo requestAnimationFrame. Se detiene con la pestaña oculta y vuelve al
 * regresar. Con prefers-reduced-motion se redibuja a ~8 fps y sin señales (la
 * preferencia se lee al montar, antes del primer avance, y se relee en vivo). El
 * DPR se topa en 2 y baja a 1.25 una sola vez si el cuadro promedio tarda más de
 * 26 ms: mismos valores que la escena de Timbrando.
 *
 * Si no hay contexto 2D, o se pierde, o dibujar revienta, el canvas se oculta, se
 * llama a `alFallar` (la vista pone su fallback CSS) y `montarOrbe` devuelve null
 * (en el primer caso) o el orbe queda inerte (en los otros): el paso nunca se queda
 * en blanco ni lanza.
 */
import { crearMotor } from '../lib/orbe-red.js';

var DPR_TOPE = 2;          // el canvas nunca pasa de 2x, aunque la pantalla dé más
var DPR_BAJO = 1.25;       // al que se baja si la máquina no da el ancho
var COSTO_MAX_MS = 26;     // cuadro promedio a partir del cual se baja el DPR
var MUESTRAS = 90;         // cuadros que se promedian para decidirlo
var CALMA_MS = 125;        // reduced-motion: ~8 fps

// El orbe de migración no atenúa su intensidad: siempre la normal.
var FX = { sat: 1, bri: 1, vel: 1, version: 0 };

export function montarOrbe(canvas, opciones) {
  var op = opciones || {};

  // La preferencia se lee ANTES de crear el motor: un avance que llega antes del
  // primer cuadro ya respeta reduced-motion (R25 a). Se relee en vivo más abajo.
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var calma = !!(mq && mq.matches);

  var motor = crearMotor(canvas, { paleta: 'cristal', semilla: op.semilla, calma: calma });
  if (!motor) {
    if (op.alFallar) op.alFallar();
    return null;
  }

  var activo = true;       // false tras detener() o un fallo: ya no vuelve a arrancar
  var corriendo = false;
  var raf = 0;
  var ultimo = 0;          // ms del último cuadro pintado
  var t = 0;               // reloj de la escena (frenado con reduced-motion)
  var real = 0;            // reloj real, en segundos desde que arrancó
  var tope = DPR_TOPE;
  var muestras = 0;
  var costo = 0;
  var sincronizado = false; // el primer avance válido tras montar se enciende en silencio (R25 b)

  // La preferencia se relee en vivo: se puede activar con el asistente abierto.
  function cambioCalma(e) { calma = e.matches; }
  if (mq && mq.addEventListener) mq.addEventListener('change', cambioCalma);

  function pedir() { raf = window.requestAnimationFrame(cuadro); }

  function cuadro(ms) {
    raf = 0;
    if (!corriendo) return;

    // Con reduced-motion se redibuja a ~8 fps: el rAF sigue siendo el reloj (se frena
    // solo con la pestaña en segundo plano), pero se salta lo que sobra.
    if (calma && ultimo && (ms - ultimo) < CALMA_MS) { pedir(); return; }

    var dt = ultimo ? (ms - ultimo) / 1000 : 0.016;
    ultimo = ms;
    dt = Math.min(dt, calma ? 0.2 : 0.05);

    var vel = calma ? 0.18 : 1;
    real += dt;
    t += dt * vel;

    try {
      motor.dibujar({ t: t, dt: dt * vel, real: real, calma: calma, fx: FX, tope: tope });
    } catch (e) {
      fallar();
      return;
    }

    // Si la máquina no da el ancho se baja la resolución una sola vez, en vez de
    // arrastrar la pantalla a tirones. No se mide con reduced-motion: ahí los 125 ms
    // entre cuadros son a propósito, no lentitud.
    if (!calma && tope === DPR_TOPE) {
      costo += dt * 1000;
      if (++muestras >= MUESTRAS) {
        if (costo / muestras > COSTO_MAX_MS) tope = DPR_BAJO;
        muestras = 0;
        costo = 0;
      }
    }

    pedir();
  }

  function arrancar() {
    if (corriendo || !activo) return;
    corriendo = true;
    ultimo = 0;
    pedir();
  }

  function pausar() {
    corriendo = false;
    if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
  }

  // Pestaña en segundo plano: el navegador ya frena el rAF, pero si la dejan
  // minimizada media hora no tiene sentido reanudar con el reloj corrido.
  function cambioVisibilidad() {
    if (document.hidden) pausar();
    else arrancar();
  }

  // Un contexto perdido (memoria de GPU, pestaña dormida mucho tiempo) no puede dejar
  // la pantalla sin señal de vida. No se llama a preventDefault a propósito: sin
  // restauración, pero con el fallback de CSS a la vista.
  function contextoPerdido() { fallar(); }

  function soltar() {
    activo = false;
    pausar();
    document.removeEventListener('visibilitychange', cambioVisibilidad);
    canvas.removeEventListener('contextlost', contextoPerdido, false);
    if (mq && mq.removeEventListener) mq.removeEventListener('change', cambioCalma);
  }

  function fallar() {
    if (!activo) return;
    soltar();
    canvas.style.display = 'none';
    if (op.alFallar) op.alFallar();
  }

  document.addEventListener('visibilitychange', cambioVisibilidad);
  canvas.addEventListener('contextlost', contextoPerdido, false);
  if (!document.hidden) arrancar();

  return {
    // Al abrir (o recargar) la pantalla con la migración avanzada, lo ya aprendido se
    // enciende sin destello ni 120 señales de golpe; lo que avanza después sí se nota.
    progreso: function (p) {
      if (!activo) return;
      motor.encender(p, !sincronizado);
      if (p >= 0) sincronizado = true;
    },
    rafaga: function () { if (activo) motor.rafaga(); },
    destello: function () { if (activo) motor.destello(); },
    detener: soltar
  };
}
